/* import __COLOCATED_TEMPLATE__ from './email-forwarding-verification-button.hbs'; */
/* RESPONSIBLE TEAM: team-channels */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type DomainService from 'embercom/services/domain-service';
import { type DomainEmailAddress } from 'embercom/services/domain-service';
import { taskFor } from 'ember-concurrency-ts';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';

interface Args {
  emailAddress: DomainEmailAddress | undefined;
}

interface Signature {
  Args: Args;
}

export default class EmailForwardingVerificationButton extends Component<Signature> {
  @service declare intercomEventService: any;
  @service declare mailForwardingValidationService: any;
  @service declare domainService: DomainService;
  @service declare realTimeEventService: $TSFixMe;

  @tracked verificationTaskResult: 'success' | 'failed' | null = null;

  get verificationTask() {
    return taskFor(this.mailForwardingValidationService.verifyEmailForwarding);
  }

  get verification() {
    return this.mailForwardingValidationService.verification;
  }

  unregisterForwardMailVerifiedListener() {
    this.realTimeEventService.off('ForwardMailVerified', this, '_handleForwardMailVerifiedMessage');
  }

  willDestroy() {
    this.unregisterForwardMailVerifiedListener();
    super.willDestroy();
  }

  @action
  async verifyEmailForwarding() {
    if (!this.args.emailAddress) {
      return;
    }
    this.realTimeEventService.on('ForwardMailVerified', this, '_handleForwardMailVerifiedMessage');
    await this.mailForwardingValidationService.verifyEmailForwarding.perform(
      this.args.emailAddress.email,
      'domains-and-addresses_edit-address',
      true,
    );
    if (!this.verification) {
      this.verificationTaskResult = 'failed';
      this.unregisterForwardMailVerifiedListener();
      this.trackEvent('failed', 'check_email_forwarding');
    }
    this.mailForwardingValidationService.unset();
  }

  _handleForwardMailVerifiedMessage() {
    this.domainService.load();
    this.verificationTaskResult = 'success';
    this.trackEvent('success', 'check_email_forwarding');
  }

  @action
  requestForwardingVerification() {
    this.verifyEmailForwarding();
    this.trackEvent('clicked', 'check_email_forwarding');
  }

  @action
  trackEvent(eventAction: string, event: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: eventAction,
      object: event,
      place: 'check-email-forwarding',
      context: 'email-setup-sidebar',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Channels::Email::DomainsAndAddresses::EmailForwardingVerificationButton': typeof EmailForwardingVerificationButton;
  }
}
